// ─────────────────────────────────────────────────────────────
// LinkedIn connection rows — one normal form, two mouths.
//
// Connections reach Omina by two sanctioned routes, and rule #5 rules out
// every other one:
//
//   1. The Member Data Portability API (EEA/Switzerland members only), whose
//      CONNECTIONS snapshot returns rows of title-cased string fields.
//   2. LinkedIn's own archive export, Connections.csv, which the member
//      downloads herself — the only route available outside the EEA.
//
// Those two produce the *same* row, because LinkedIn generates both from the
// same archive: the snapshot's `snapshotData` keys are the CSV's header row.
// That is confirmed for the PROFILE domain and inferred for CONNECTIONS —
// LinkedIn documents no field table for it. So the normaliser below matches
// keys loosely and reports the ones it did not recognise, which turns a wrong
// guess into a one-line fix instead of an afternoon.
// ─────────────────────────────────────────────────────────────

/** A connection in Omina's own shape, whichever mouth it came through. */
export interface ConnectionRow {
  /** Display name — always present; the row is dropped without one. */
  name: string;
  url?: string | null;
  email?: string | null;
  company?: string | null;
  position?: string | null;
  /** ISO date (YYYY-MM-DD). The day the connection was made. */
  connectedOn?: string | null;
}

export interface NormalizeResult {
  rows: ConnectionRow[];
  /** Rows that carried no usable name. Counted, never silently dropped. */
  skipped: number;
  /**
   * Keys present in the input that no alias matched. Empty in the happy path.
   * Surfaced to the founder rather than swallowed: if LinkedIn renames a
   * column, this is the difference between "0 connections found" (a mystery)
   * and "we saw a column called X" (a fix).
   */
  unknownKeys: string[];
}

/** Keys are compared with punctuation and spacing removed, so "First Name",
 *  "first_name" and "firstname" are the same key. */
function keyOf(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, "");
}

const ALIASES = {
  firstName: ["firstname", "givenname"],
  lastName: ["lastname", "familyname", "surname"],
  fullName: ["name", "fullname", "membername"],
  url: ["url", "profileurl", "publicprofileurl", "linkedinurl", "profilelink"],
  email: ["emailaddress", "email", "primaryemail"],
  company: ["company", "companyname", "organization", "organisation"],
  position: ["position", "title", "jobtitle", "headline", "role"],
  connectedOn: ["connectedon", "connectiondate", "dateconnected", "connected"],
} as const;

type Field = keyof typeof ALIASES;

/** Reverse index: normalised key → field. Built once. */
const FIELD_BY_KEY = new Map<string, Field>();
for (const [field, keys] of Object.entries(ALIASES) as [Field, readonly string[]][]) {
  for (const k of keys) FIELD_BY_KEY.set(k, field);
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * LinkedIn writes `Connected On` as "12 Mar 2026". Some exports use
 * "Mar 12, 2026", and the API may hand back an ISO string.
 *
 * Parsed by hand rather than with `Date.parse`, which is locale- and
 * engine-dependent for these formats — "03/04/2026" is March in one runtime
 * and April in another, and a connection dated three weeks off silently
 * matches the wrong event. Ambiguous numeric formats are refused outright:
 * a missing date shows an honest empty state, a wrong one does not.
 */
export function parseConnectedOn(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;

  // Already ISO.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // "12 Mar 2026"
  const dmy = raw.match(/^(\d{1,2})\s+([A-Za-z]{3,})\.?\s+(\d{4})$/);
  if (dmy) {
    const month = MONTHS[dmy[2]!.slice(0, 3).toLowerCase()];
    if (month) return isoDate(Number(dmy[3]), month, Number(dmy[1]));
  }

  // "Mar 12, 2026"
  const mdy = raw.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/);
  if (mdy) {
    const month = MONTHS[mdy[1]!.slice(0, 3).toLowerCase()];
    if (month) return isoDate(Number(mdy[3]), month, Number(mdy[2]));
  }

  return null;
}

function isoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * Canonical form of a LinkedIn profile URL, used as the identity key when
 * merging an import into contacts that already exist.
 *
 * `https://www.linkedin.com/in/ana-silva-7b3a/?originalSubdomain=br` and
 * `linkedin.com/in/ana-silva-7b3a` are the same person, and a founder who
 * re-imports every month must not accumulate four copies of her. Returns null
 * for anything that isn't a profile URL, so the caller falls back to a name
 * match rather than keying on garbage.
 */
export function normalizeLinkedinUrl(url?: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withProtocol);
  } catch {
    return null;
  }
  if (!/(^|\.)linkedin\.com$/i.test(parsed.hostname)) return null;

  const slug = parsed.pathname.match(/\/in\/([^/]+)/i)?.[1];
  if (!slug) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(slug);
  } catch {
    // Malformed percent-escapes — use the raw slug rather than dropping the
    // row; a slightly-off key still dedupes against itself consistently.
    decoded = slug;
  }
  return `linkedin.com/in/${decoded.toLowerCase()}`;
}

/** Unicode combining marks. Built from a string rather than written as a
 *  literal range: the literal is invisible in an editor, and the next person
 *  to touch this line would be editing by braille. */
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");

/** Folded form of a person's name, the fallback identity when a contact was
 *  added by hand and has no LinkedIn URL. Diacritics are stripped so
 *  "João Alvares" and "Joao Alvares" are one person. */
export function normalizeName(name?: string | null): string | null {
  if (!name) return null;
  const folded = name
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return folded || null;
}

/**
 * True when this contact's *own* words say they invest.
 *
 * Keyed on the contact's role and company, never on an industry — rule #3.
 * It only decides whether to spend one extra search on investment thesis, so
 * a false positive costs a query and a false negative costs a little context.
 */
export function looksLikeInvestor(contact: {
  role?: string | null;
  company?: string | null;
}): boolean {
  const text = `${contact.role ?? ""} ${contact.company ?? ""}`.toLowerCase();
  if (!text.trim()) return false;
  return /\b(investor|investing|vc|venture|angel|capital|ventures|partner|lp|gp|fund|funds)\b/.test(
    text,
  );
}

/**
 * Raw rows (snapshot `snapshotData` entries or parsed CSV records) → the
 * normal form.
 *
 * Every value is coerced to a trimmed string and empty strings become null:
 * LinkedIn pads absent fields with `""`, and a contact whose email is `""`
 * rather than null would render as having an email and break a mailto.
 */
export function normalizeConnections(raw: unknown): NormalizeResult {
  const rows: ConnectionRow[] = [];
  const unknown = new Set<string>();
  let skipped = 0;

  const list = Array.isArray(raw) ? raw : [];
  for (const entry of list) {
    if (!entry || typeof entry !== "object") {
      skipped++;
      continue;
    }

    const picked: Partial<Record<Field, string>> = {};
    for (const [rawKey, rawValue] of Object.entries(entry as Record<string, unknown>)) {
      const field = FIELD_BY_KEY.get(keyOf(rawKey));
      if (!field) {
        unknown.add(rawKey);
        continue;
      }
      const value = typeof rawValue === "string" ? rawValue.trim() : "";
      // First alias wins, so "Name" never overwrites a real "First Name".
      if (value && picked[field] === undefined) picked[field] = value;
    }

    const name =
      [picked.firstName, picked.lastName].filter(Boolean).join(" ").trim() ||
      picked.fullName ||
      "";
    if (!name) {
      skipped++;
      continue;
    }

    rows.push({
      name,
      url: picked.url ?? null,
      email: picked.email ?? null,
      company: picked.company ?? null,
      position: picked.position ?? null,
      connectedOn: parseConnectedOn(picked.connectedOn) ?? null,
    });
  }

  return { rows, skipped, unknownKeys: [...unknown] };
}
